<template>
  <div v-on-click-outside="handleClickOutside" class="base-dropdown">
    <input
      v-if="name"
      class="hidden"
      :name="name"
      :value="modelValue"
      tabindex="-1"
      :aria-labelledby="ariaLabelledby"
      :aria-label="ariaLabelledby ? undefined : ariaLabel"
    />
    <slot
      name="trigger"
      :keydown="handleKeyDown"
      :keyup="handleKeyUp"
      :toggle="handleToggle"
      :is-open="isOpen"
      :selected-option="selectedOption"
    >
      <button
        :id="id"
        :data-test-id="dataTestId"
        class="base-dropdown__button"
        :disabled="disabled"
        :class="[buttonClass, { error }]"
        type="button"
        :role="ariaButtonRole"
        :aria-haspopup="ariaButtonHasPopup"
        :aria-controls="dropdownElId"
        :aria-activedescendant="a11yEnabled ? ariaActiveDescendant : undefined"
        :aria-disabled="disabled"
        :aria-required="required || undefined"
        :aria-invalid="error || undefined"
        :aria-labelledby="ariaLabelledby"
        :aria-label="ariaLabelledby ? undefined : ariaLabel"
        :aria-expanded="isOpen"
        :tabindex="tabindex"
        @click="handleToggle"
        @keydown="handleKeyDown"
        @keyup.prevent="handleKeyUp"
      >
        <slot name="button-content" :selected-option="selectedOption" :placeholder="placeholder">
          <i v-if="icon" class="icon" :class="icon" aria-hidden="true"></i>
          <span v-if="selectedOption">{{ getOptionLabel(selectedOption) }}</span>
          <span v-else class="base-dropdown__button--placeholder">{{ placeholder }}</span>
        </slot>
      </button>
    </slot>

    <div
      :id="dropdownElId"
      ref="dropdownRef"
      class="base-dropdown__dropdown"
      :class="{ 'base-dropdown__dropdown--opened': isOpen }"
      :role="(ariaPopupRole as string) || undefined"
      :aria-hidden="!isOpen"
      :aria-labelledby="ariaLabelledby"
      :aria-label="ariaLabelledby ? undefined : ariaLabel"
      tabindex="-1"
    >
      <slot
        name="dropdown-content"
        :options="options"
        :handle-select="handleSelect"
        :focused-option="focusedOption"
        :close="handleClose"
        :handle-mouse-down="handleMouseDown"
        :handle-mouse-up="handleMouseUp"
      >
        <template v-for="option in options" :key="getOptionKey(option)">
          <div
            :id="getOptionKey(option)"
            ref="optionsRef"
            :aria-selected="isSelected(option)"
            role="option"
            class="base-dropdown__option"
            :class="{
              'base-dropdown__option--hover': isFocused(option),
            }"
            :tabindex="-1"
            @click.prevent="handleSelect(option)"
            @keyup.prevent="handleKeyUp"
            @mousedown="handleMouseDown"
            @mouseup="handleMouseUp"
          >
            <slot name="option" :option="option">
              {{ getOptionLabel(option) }}
            </slot>
          </div>
        </template>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import type { Props, DropdownModelValue, BaseDropdownOption } from './types';

const props = withDefaults(defineProps<Props>(), {
  options: () => [],
  disabled: false,
  error: false,
  required: false,
  placeholder: '',
  icon: 'icon-Down',
  tabindex: 0,
  optionValueKey: 'value',
  optionLabelKey: 'text',
  optionKeyKey: 'itemId',
  a11yEnabled: true,
  ariaPopupRole: 'listbox',
});

const emit = defineEmits<{
  'update:modelValue': [value: DropdownModelValue];
  open: [];
  close: [];
  select: [option: BaseDropdownOption | string];
}>();

const ARROW_UP = 'ArrowUp';
const ARROW_DOWN = 'ArrowDown';
const ENTER = 'Enter';
const ESC = 'Escape';
const TAB = 'Tab';

const isOpen = ref(false);
const focusedOption = ref<BaseDropdownOption | string | null>(null);
const a11yFocusIndex = ref(-1);
const isHoldClick = ref(false);
const isArrowScrollDisabled = ref(false);
const optionsRef = ref<HTMLElement[]>([]);
const dropdownRef = ref<HTMLElement | null>(null);

const getOptionValue = (option: BaseDropdownOption | string): unknown => {
  return typeof option === 'object' ? option[props.optionValueKey!] : option;
};

const getOptionLabel = (option: BaseDropdownOption | string): unknown => {
  return typeof option === 'object' ? option[props.optionLabelKey!] : option;
};

const getOptionKey = (option: BaseDropdownOption | string): string => {
  if (typeof option === 'object') {
    const key = option[props.optionKeyKey!];
    return (key ?? getOptionValue(option)) as string;
  }
  return option;
};

const ariaButtonRole = computed(() => {
  if (props.ariaPopupRole === 'listbox') return 'combobox';
  return undefined;
});

const dropdownElId = computed(() => {
  if (props.ariaPopupRole && props.id) return `${props.id}-listbox`;
  return undefined;
});

const ariaActiveDescendant = computed(() => {
  if (!isOpen.value || !focusedOption.value) return undefined;
  return getOptionKey(focusedOption.value);
});

const ariaButtonHasPopup = computed(
  () =>
    (props.ariaPopupRole ? props.ariaPopupRole : undefined) as
      | 'listbox'
      | 'dialog'
      | 'menu'
      | 'tree'
      | 'grid'
      | boolean
      | undefined
);

const selectedOption = computed(
  () => props.options?.find((item) => getOptionValue(item) === props.modelValue)
);

const isSelected = (option: BaseDropdownOption | string): boolean => {
  return getOptionValue(option) === props.modelValue;
};

const isFocused = (option: BaseDropdownOption | string): boolean => {
  return !!(focusedOption.value && getOptionValue(option) === getOptionValue(focusedOption.value));
};

const handleToggle = (): void => {
  if (props.disabled) return;

  if (isOpen.value) {
    handleClose();
  } else {
    handleOpen();
  }
};

const handleOpen = (): void => {
  isOpen.value = true;
  isArrowScrollDisabled.value = true;

  if (selectedOption.value) {
    const selectedIndex = props.options?.findIndex(
      (item) => getOptionValue(item) === props.modelValue
    );
    a11yFocusIndex.value = selectedIndex ?? -1;
    focusedOption.value = selectedOption.value;
    scrollToActiveListItem();
  }

  emit('open');
};

const handleClose = (): void => {
  isOpen.value = false;
  isArrowScrollDisabled.value = false;
  focusedOption.value = null;
  a11yFocusIndex.value = -1;
  emit('close');
};

const handleMouseDown = (): void => {
  isHoldClick.value = true;
};

const handleMouseUp = (): void => {
  isHoldClick.value = false;
};

const handleArrowNavigation = (code: string): void => {
  if (
    code === ARROW_DOWN &&
    (a11yFocusIndex.value < (props.options?.length ?? 0) - 1 || a11yFocusIndex.value === -1)
  ) {
    a11yFocusIndex.value = a11yFocusIndex.value + 1;
  }

  if (code === ARROW_UP && a11yFocusIndex.value > 0) {
    a11yFocusIndex.value = a11yFocusIndex.value - 1;
  }

  focusedOption.value = props.options?.[a11yFocusIndex.value] ?? null;
  scrollToActiveListItem();
};

const handleEnterKeyUp = (): void => {
  if (!isOpen.value || !focusedOption.value) {
    handleToggle();
    return;
  }

  const focusedEl = dropdownRef.value?.querySelector('.base-dropdown__option--hover');
  if (focusedEl) {
    (focusedEl as HTMLElement).click();
  }
};

const handleKeyUp = ($event: KeyboardEvent): void => {
  if ($event.code === ESC) {
    handleClose();
    return;
  }

  if ($event.code === ARROW_UP || $event.code === ARROW_DOWN) {
    handleArrowNavigation($event.code);
  }

  if ($event.code === ENTER) {
    handleEnterKeyUp();
  }
};

const handleKeyDown = ($event: KeyboardEvent): void => {
  if ($event.code === ENTER) {
    $event.preventDefault();
  }
  if (!isArrowScrollDisabled.value) return;
  if ($event.code === ARROW_DOWN || $event.code === ARROW_UP) {
    $event.preventDefault();
  }
  if ($event.code === TAB) {
    handleClickOutside();
  }
};

const handleSelect = (option: BaseDropdownOption | string): void => {
  if (props.customSelect) {
    props.customSelect(option);
  } else {
    const value = getOptionValue(option);
    emit('update:modelValue', value as string | number | object);
    emit('select', option);
  }
  handleClose();
  focusedOption.value = option;
};

const handleClickOutside = (): void => {
  if (isOpen.value && !isHoldClick.value) {
    handleClose();
  }
};

const scrollToActiveListItem = (): void => {
  if (!dropdownRef.value) return;
  nextTick(() => {
    let el: HTMLElement | undefined;
    if (optionsRef.value?.length) {
      el = optionsRef.value.find(
        (option) => option?.classList?.contains('base-dropdown__option--hover')
      );
    }
    if (!el) {
      el = dropdownRef.value?.querySelector('.base-dropdown__option--hover') as
        | HTMLElement
        | undefined;
    }

    if (el) {
      dropdownRef.value?.scrollTo({
        top: el.offsetTop,
        behavior: 'smooth',
      });
    }
  });
};

defineExpose({
  isOpen,
  focusedOption,
  open: handleOpen,
  close: handleClose,
  toggle: handleToggle,
});
</script>

<style lang="scss">
@import './scss/BaseDropdown.scss';
</style>
